"use client"
import { API_BASE_URL } from "../utils/Constant"
import { useState, useEffect, useRef } from "react"
import { gsap } from "gsap"
import axios from "axios"
import HomeNavbar from "./HomeNavbar"
import Footer from "./Footer"

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState("");

  const formRef = useRef(null);
  const backgroundRef = useRef(null);
  const chartRef = useRef(null);
  const candlesRef = useRef(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus("Submitting...");

    try {
      await axios.post(`${API_BASE_URL}/contact`, formData, {
        headers: { "Content-Type": "application/json" },
      });
      setStatus("Your message has been sent!");
      setFormData({ name: "", email: "", mobile: "", message: "" });
    } catch (error) {
      setStatus("Oops! Something went wrong. Please try again.");
    }
    setIsSubmitting(false);
  };

  useEffect(() => {
    // Background animations (same as HomePage.js)
    const tl = gsap.timeline({ repeat: -1 });
    gsap.set(".chart-line", {
      stroke: "rgba(59, 130, 246, 0.2)",
      strokeDasharray: 1000,
      strokeDashoffset: 1000,
    });
    gsap.set(".candle", {
      scaleY: 0,
      transformOrigin: "bottom",
    });
    tl.to(".chart-line", {
      strokeDashoffset: 0,
      duration: 2,
      stagger: 0.1,
      ease: "power1.inOut",
    });
    tl.to(
      ".candle",
      {
        scaleY: 1,
        duration: 0.5,
        stagger: 0.1,
        ease: "back.out(1.7)",
      },
      "-=1.5"
    );
    gsap.to(".bg-pattern", {
      x: "10%",
      y: "5%",
      duration: 20,
      repeat: -1,
      yoyo: true,
      ease: "sine.inOut",
    });

    // Form animation
    const formElement = formRef.current;
    const formChildren = formElement?.children || [];
    gsap.set(formChildren, { y: 50, opacity: 0 });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            gsap.to(formChildren, {
              y: 0,
              opacity: 1,
              duration: 0.8,
              stagger: 0.2,
              ease: "power3.out",
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (formElement) observer.observe(formElement);

    return () => {
      tl.kill();
      gsap.killTweensOf(".bg-pattern");
      if (formElement) observer.unobserve(formElement);
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 text-white overflow-hidden">
      <HomeNavbar />

      {/* SVG Background from HomePage */}
      <div ref={backgroundRef} className="fixed inset-0 opacity-20 pointer-events-none bg-pattern">
        <div ref={chartRef} className="absolute inset-0">
          <svg width="100%" height="100%" viewBox="0 0 1000 1000" preserveAspectRatio="none">
            {[...Array(20)].map((_, i) => {
              const y1 = Number((800 - i * 30 + Math.sin(i) * 50).toFixed(2)) || 0;
              const x2 = Number((250 + i * 20).toFixed(2)) || 0;
              const y2 = Number((700 - Math.cos(i) * 100).toFixed(2)) || 0;
              const x3 = Number((500 - i * 10).toFixed(2)) || 0;
              const y3 = Number((600 + Math.sin(i) * 80).toFixed(2)) || 0;
              const x4 = Number((750 + i * 5).toFixed(2)) || 0;
              const y4 = Number((500 - Math.cos(i) * 60).toFixed(2)) || 0;
              const y5 = Number((400 + Math.sin(i) * 100).toFixed(2)) || 0;

              const coordinates = [y1, x2, y2, x3, y3, x4, y4, y5];
              if (!coordinates.every(coord => Number.isFinite(coord))) {
                console.error(`Invalid coordinates for path ${i}:`, { y1, x2, y2, x3, y3, x4, y4, y5 });
                return null;
              }

              const d = `M 0 ${y1} C ${x2} ${y2} ${x3} ${y3} ${x4} ${y4} L 1000 ${y5}`;

              return (
                <path
                  key={`line-${i}`}
                  className="chart-line"
                  d={d}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              );
            })}
          </svg>
        </div>
        <div ref={candlesRef} className="absolute inset-0">
          <svg width="100%" height="100%" viewBox="0 0 1000 500">
            {[...Array(30)].map((_, i) => {
              const x = 100 + i * 30;
              const height = 20 + Math.random() * 80;
              const width = 8;
              const isUp = Math.random() > 0.5;
              const color = isUp ? "rgba(16, 185, 129, 0.7)" : "rgba(239, 68, 68, 0.7)";
              const wickHeight = height * 0.6;
              return (
                <g key={`candle-${i}`}>
                  <line x1={x} y1={250 - wickHeight} x2={x} y2={250 + wickHeight} stroke={color} strokeWidth="1" />
                  <rect
                    className="candle"
                    x={x - width / 2}
                    y={isUp ? 250 - height : 250}
                    width={width}
                    height={height}
                    fill={color}
                    rx="1"
                  />
                </g>
              );
            })}
          </svg>
        </div>
      </div>

      {/* Contact Form Section */}
      <section className="relative z-10 min-h-screen flex items-center justify-center py-16 px-4 md:px-6 lg:px-8">
        <div
          ref={formRef}
          className="w-full max-w-md bg-slate-700/50 backdrop-blur-sm p-6 rounded-lg shadow-lg"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-6">
            Get In Touch
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-lg font-medium text-slate-200 mb-2">
                Your Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-slate-800 text-white border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-lg font-medium text-slate-200 mb-2">
                Your Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-slate-800 text-white border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Mobile Field */}
            <div>
              <label htmlFor="mobile" className="block text-lg font-medium text-slate-200 mb-2">
                Your Mobile Number
              </label>
              <input
                type="text"
                id="mobile"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 bg-slate-800 text-white border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Message Field */}
            <div>
              <label htmlFor="message" className="block text-lg font-medium text-slate-200 mb-2">
                Your Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="4"
                className="w-full px-4 py-2 bg-slate-800 text-white border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              ></textarea>
            </div>

            {/* Status Message */}
            {status && (
              <p
                className={`text-lg font-semibold text-center ${
                  status.includes("sent") ? "text-green-400" : "text-red-400"
                }`}
              >
                {status}
              </p>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-2 font-bold rounded-lg text-lg transition-colors ${
                isSubmitting
                  ? "bg-slate-600 text-slate-300 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              {isSubmitting ? "Sending..." : "Send Message"}
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactForm;